import React, { useState, useEffect } from 'react';
import { Monitor, Smartphone, Tablet, Clock, MapPin, Shield, LogOut, Trash2, AlertTriangle } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';
import useToast from '../../hooks/useToast';

export default function Sessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);
  const [showRevokeAll, setShowRevokeAll] = useState(false);

  useEffect(() => { loadSessions(); }, []);
  const toast = useToast();
  const loadSessions = async () => {
    try {
      const res = await api.get('/api/auth/sessions');
      setSessions(res.data.sessions || []);
    } catch {} finally { setLoading(false); }
  };

  const handleRevoke = async (sessionId) => {
    setRevoking(sessionId);
    try {
      await api.delete(`/api/auth/sessions/${sessionId}`);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      toast.success('Session revoked', {
        id: `session-revoked-${sessionId}`,
        duration: 4000,
      });
    } catch { toast.error('Failed to revoke session', { id: 'session-revoke-error' }); }
    finally { setRevoking(null); }
  };

  const handleRevokeAll = async () => {
    setShowRevokeAll(false);
    setRevoking('all');
    try {
      await api.delete('/api/auth/sessions');
      await loadSessions();
      toast.success('All other sessions revoked', { id: 'session-revoke-all-success' });
    } catch { toast.error('Failed to revoke sessions', { id: 'session-revoke-all-error' }); }
    finally { setRevoking(null); }
  };

  const getDeviceIcon = (device) => {
    const d = device?.toLowerCase() || '';
    if (d.includes('phone') || d.includes('mobile') || d.includes('iphone') || d.includes('android')) return <Smartphone size={20} />;
    if (d.includes('tablet') || d.includes('ipad')) return <Tablet size={20} />;
    return <Monitor size={20} />;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr); const now = new Date(); const diff = now - date;
    if (diff < 60000) return 'Active now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 60 }}><div className="loader"></div></div>;
  }

  return (
    <div style={{ maxWidth: '100%', margin: '0 auto' }}>
      <div style={{ marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 700, margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Shield size={16} /> Session Management
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 0 }}>Manage your active sessions across devices.</p>
        </div>
        {sessions.length > 1 && (
          <button onClick={() => setShowRevokeAll(true)} style={{
            padding: '8px 16px', borderRadius: 6, border: '1px solid var(--error)',
            background: 'var(--error-bg)', color: 'var(--error)',
            cursor: 'pointer', fontSize: 12, fontWeight: 500,
            display: 'flex', alignItems: 'center', gap: 4,
          }}>
            <LogOut size={16} /> Revoke All Others
          </button>
        )}
      </div>

      {sessions.filter(s => s.is_current).map(session => (
        <div key={session.id} style={{
          background: 'linear-gradient(135deg, var(--success-bg), rgba(63,185,80,0.02))',
          borderRadius: 14, padding: '20px 24px', border: '1px solid rgba(63,185,80,0.2)', marginBottom: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ color: 'var(--success)' }}>{getDeviceIcon(session.device_name)}</span>
              <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 15 }}>{session.device_name}</span>
              <span style={{ background: 'var(--success-bg)', color: 'var(--success)', padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600 }}>This device</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginTop: 8 }}>
            <SessionDetail icon="🌐" label="Browser" value={session.browser} />
            <SessionDetail icon="💻" label="OS" value={session.os} />
            <SessionDetail icon={<MapPin size={12} />} label="IP" value={session.ip_address} />
            <SessionDetail icon={<Clock size={12} />} label="Last Active" value={formatTime(session.last_active)} />
          </div>
        </div>
      ))}

      {sessions.filter(s => !s.is_current).length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h3 style={{ color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Other Devices</h3>
          {sessions.filter(s => !s.is_current).map(session => (
            <div key={session.id} style={{
              background: 'var(--bg-secondary)', borderRadius: 12, padding: '18px 22px',
              border: '1px solid var(--border-primary)', marginBottom: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12,
            }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ color: 'var(--text-secondary)' }}>{getDeviceIcon(session.device_name)}</span>
                  <span style={{ color: 'var(--text-primary)', fontWeight: 500, fontSize: 14 }}>{session.device_name}</span>
                </div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                  <SessionDetail icon="🌐" label="" value={session.browser} />
                  <SessionDetail icon={<Clock size={11} />} label="" value={formatTime(session.last_active)} />
                  <SessionDetail icon={<MapPin size={11} />} label="" value={session.ip_address} />
                </div>
              </div>
              <button onClick={() => handleRevoke(session.id)} disabled={revoking === session.id} style={{
                padding: '8px 16px', borderRadius: 6, border: '1px solid var(--error)',
                background: 'transparent', color: 'var(--error)',
                cursor: 'pointer', fontSize: 12, fontWeight: 500,
                display: 'flex', alignItems: 'center', gap: 4,
              }}>
                {revoking === session.id ? '...' : <><Trash2 size={13} /> Revoke</>}
              </button>
            </div>
          ))}
        </div>
      )}

      {sessions.length === 0 && (
        <div style={{ textAlign: 'center', padding: 50, color: 'var(--text-secondary)' }}>
          <Shield size={48} style={{ marginBottom: 16, opacity: 0.3 }} />
          <p>No active sessions found.</p>
        </div>
      )}

      {showRevokeAll && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }} onClick={() => setShowRevokeAll(false)}>
          <div style={{ background: 'var(--bg-primary)', borderRadius: 16, padding: '28px 32px', maxWidth: 420, width: '100%', border: '1px solid var(--error-bg)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <AlertTriangle size={40} color="var(--error)" style={{ marginBottom: 12 }} />
              <h3 style={{ color: 'var(--text-primary)', fontSize: 18, margin: '0 0 8px' }}>Revoke All Other Sessions?</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 0 }}>You'll be logged out from all other devices. Your current session will remain active.</p>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => setShowRevokeAll(false)} style={{ flex: 1, padding: '12px', borderRadius: 8, border: '1px solid var(--border-primary)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
              <button onClick={handleRevokeAll} style={{ flex: 1, padding: '12px', borderRadius: 8, border: 'none', background: 'var(--error)', color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Revoke All</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SessionDetail({ icon, label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--text-secondary)', fontSize: 12 }}>
      {typeof icon === 'string' ? <span>{icon}</span> : icon}
      {label && <span>{label}:</span>}
      <span style={{ color: 'var(--text-secondary)' }}>{value}</span>
    </div>
  );
}